import React, { useEffect, useState } from 'react';
import { authAPI } from '../services/api';
import { ModernCard, ModernTable, ModernSelect, ModernButton, ModernLoadingSpinner, ModernSearchBar } from '../components/ui/ModernComponents';

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee', label: 'Employé' },
];

const Privileges = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [updating, setUpdating] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [stores, setStores] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await authAPI.getEmployees();
        setUsers(res.data);
      } catch {
        setUsers([]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    authAPI.getStores().then(res => setStores(res.data)).catch(() => setStores([]));
  }, []);

  const filteredUsers = users.filter(u =>
    (!search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())) &&
    (!roleFilter || u.role === roleFilter) &&
    (!storeFilter || String(u.store_id) === String(storeFilter))
  );

  const handleRoleChange = async (id, selectedOption) => {
    setUpdating(id);
    try {
      const user = users.find(u => u.id === id);
      if (!user) throw new Error('Utilisateur introuvable');
      
      // Extract the value from the selected option (react-select passes the option object)
      const newRole = selectedOption ? selectedOption.value : null;
      if (!newRole) return;
      
      // Only include phone if valid
      let updatePayload = {
        name: user.name,
        email: user.email,
        store_id: user.store_id,
        is_active: user.is_active,
        role: newRole
      };
      if (user.phone && /^\+?\d{8,15}$/.test(user.phone)) {
        updatePayload.phone = user.phone;
      }
      await authAPI.updateEmployee(id, updatePayload);
      setUsers(users => users.map(u => u.id === id ? { ...u, role: newRole } : u));
    } catch (err) {
      alert('Erreur lors de la mise à jour du rôle: ' + (err?.response?.data?.message || 'Erreur serveur'));
    } finally {
      setUpdating(null);
    }
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nom' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Rôle', render: (v, u) => (
      <ModernSelect
        value={roleOptions.find(option => option.value === u.role)}
        onChange={selectedOption => handleRoleChange(u.id, selectedOption)}
        options={roleOptions}
        disabled={updating === u.id}
        loading={updating === u.id}
      />
    ) },
    { key: 'store_name', label: 'Magasin' },
    { key: 'is_active', label: 'Actif', render: v => v ? 'Oui' : 'Non' },
  ];

  return (
    <div className="space-y-8 p-6">
      <h1 className="text-2xl font-bold mb-4">Gestion des privilèges</h1>
      <ModernCard className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <ModernSearchBar placeholder="Recherche nom ou email..." onSearch={setSearch} />
          <ModernSelect 
            label="Rôle" 
            value={roleFilter ? { value: roleFilter, label: roleOptions.find(opt => opt.value === roleFilter)?.label || roleFilter } : null} 
            onChange={selectedOption => setRoleFilter(selectedOption ? selectedOption.value : '')} 
            options={[{ value: '', label: 'Tous' }, ...roleOptions]} 
          />
          <ModernSelect 
            label="Magasin" 
            value={storeFilter ? { value: storeFilter, label: stores.find(s => s.id == storeFilter)?.name || storeFilter } : null} 
            onChange={selectedOption => setStoreFilter(selectedOption ? selectedOption.value : '')} 
            options={[{ value: '', label: 'Tous' }, ...stores.map(s => ({ value: s.id, label: s.name }))]} 
          />
        </div>
      </ModernCard>
      <ModernCard className="p-4">
        {loading ? (
          <ModernLoadingSpinner size="lg" />
        ) : (
          <ModernTable columns={columns} data={filteredUsers} className="w-full" />
        )}
      </ModernCard>
    </div>
  );
};

export default Privileges; 